import { Component, Input } from '@angular/core';
import { PhoneTypes } from 'generated/graphql-types';

import { DeviceDetailView } from 'src/app/pages/device-details/models/device-detail-view.model';
import { DeviceRegisterListItem } from 'src/app/pages/device-register/models/device-register-results.model';
import {
   ExtensionHackedFraudDetails,
   HackedPhonesDetails,
} from 'src/app/pages/extension-details/helpers/hacked-fraud-helper';
import { ExtensionRegisterListItem } from 'src/app/pages/extension-register/models/extension-register-results.model';
import { PhoneRegisterListItem } from 'src/app/pages/phones/models/phone-register-results.model';

@Component({
    selector: 'phone-issues-chip-and-menu',
    styleUrls: ['./phone-issues-chip-and-menu.component.scss'],
    templateUrl: './phone-issues-chip-and-menu.component.html',
    standalone: false
})
export class PhoneIssuesChipAndMenuComponent {
   private _device: DeviceDetailView | DeviceRegisterListItem | null;
   private _hackedFraudDetails: ExtensionHackedFraudDetails | null;
   private _extension: ExtensionRegisterListItem | null;
   private _onlyDisplayInstructions: boolean | null;
   private _phone: PhoneRegisterListItem | null;

   @Input() set device(value: DeviceDetailView | DeviceRegisterListItem | null) {
      this._device = value;
      if (this._device) {
         this.resetVariables();
         this.checkDeviceIssues();
      }
   }
   get device(): DeviceDetailView | DeviceRegisterListItem | null {
      return this._device;
   }

   @Input() set hackedFraudDetails(value: ExtensionHackedFraudDetails | null) {
      this._hackedFraudDetails = value;
      if (this._hackedFraudDetails && !this._onlyDisplayInstructions) {
         this.resetVariables();
         this.checkHackedFraudDetails();
      }
   }
   get hackedFraudDetails(): ExtensionHackedFraudDetails | null {
      return this._hackedFraudDetails;
   }

   @Input() set extension(value: ExtensionRegisterListItem | null) {
      this._extension = value;

      if (this._extension) {
         this.resetVariables();
         this.checkExtensionIssues();
      }
   }
   get extension(): ExtensionRegisterListItem | null {
      return this._extension;
   }

   @Input() set onlyDisplayInstructions(value: boolean | null) {
      this._onlyDisplayInstructions = value;
   }

   get onlyDisplayInstructions(): boolean | null {
      return this._onlyDisplayInstructions;
   }

   @Input() set phone(value: PhoneRegisterListItem | null) {
      this._phone = value;
      if (this._phone && this._onlyDisplayInstructions) {
         this.resetVariables();
         this.setFixInstructionsForPhone(this._phone);
      }
   }
   get phone(): PhoneRegisterListItem | null {
      return this._phone;
   }

   //TODO: Potentially add more specific instructions for each issue type

   fixPhoneIssuesInstructionsHacked: string =
      'Click the "Resolve issue" button in the 3-dot menu to the right to reset the status.';
   fixPhoneIssuesInstructionsFraud: string =
      'Click the "Resolve issue" button in the 3-dot menu to the right to reset the status.';
   fixPhoneIssuesInstructionsHackedHardphone: string =
      'Click the "Resolve issue" button in the 3-dot menu to the right to reset the status and then reboot the phone.';
   fixPhoneIssuesInstructionsFraudHardphone: string =
      'Click the "Resolve issue" button in the 3-dot menu to the right to reset the status and then reboot the phone.';

   fixPhoneInstructionsString: string | null = null;
   issuesPhonesArray: HackedPhonesDetails[] = [];
   issueExtension: string | null = null;
   issuesLabel: string | null = null;

   private checkDeviceIssues() {
      if (this.device && (this.device.isHacked || this.device.isFraud)) {
         this.issuesLabel = 'Extension with issue:';
         this.issueExtension = this.device.hackedOrFraudExtension || null;

         const isHacked = this.device.isHacked || false;
         const isFraud = this.device.isFraud || false;
         this.setFixInstructionsForDevice(isHacked, isFraud, this.device.hackedOrFraudExtension);
      }
   }

   private resetVariables() {
      this.fixPhoneInstructionsString = null;
      this.issuesPhonesArray = [];
      this.issueExtension = null;
      this.issuesLabel = null;
   }

   private checkHackedFraudDetails() {
      if (this.hackedFraudDetails) {
         const hackedPhones: HackedPhonesDetails[] = this.hackedFraudDetails.hackedPhones || [];
         this.issuesLabel = hackedPhones.length > 1 ? 'Phones with issues:' : 'Phone with issue:';
         this.issuesPhonesArray.push(...hackedPhones);

         this.setFixInstructions(
            this.hackedFraudDetails.isHacked || false,
            this.hackedFraudDetails.isFraud || false,
            hackedPhones
         );
      }
   }

   private checkExtensionIssues() {
      if (this.extension) {
         const hackedOrFraudPhones: HackedPhonesDetails[] = this.extension.hackedOrFraudPhones || [];
         this.issuesLabel = hackedOrFraudPhones.length > 1 ? 'Phones with issues:' : 'Phone with issue:';
         this.issuesPhonesArray.push(...hackedOrFraudPhones);

         const isHacked = this.extension.isHacked || false;
         const isFraud = this.extension.isFraud || false;
         this.setFixInstructions(isHacked, isFraud, hackedOrFraudPhones);
      }
   }

   private setFixInstructionsForDevice(isHacked: boolean, isFraud: boolean, issueExtension: string | null | undefined) {
      if (isHacked) {
         this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsHackedHardphone;
      } else if (isFraud) {
         this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsFraudHardphone;
      }

      this.issueExtension = issueExtension ?? null;
   }

   private setInstructionsBasedOnPhoneType(
      phoneType: PhoneTypes,
      isHacked: boolean,
      isFraud: boolean,
      includeRebootInstructionsIfHardphone: boolean
   ) {
      if (phoneType === PhoneTypes.Hardphone) {
         if (includeRebootInstructionsIfHardphone) {
            if (isHacked) {
               this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsHacked;
            } else if (isFraud) {
               this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsFraud;
            }

            if (includeRebootInstructionsIfHardphone) {
               const hardphoneInstruction = ' Please reboot the hardphone(s) listed below.';

               this.fixPhoneInstructionsString = `${this.fixPhoneInstructionsString} ${hardphoneInstruction}`;
            }
         } else {
            if (isHacked) {
               this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsHackedHardphone;
            } else if (isFraud) {
               this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsFraudHardphone;
            }
         }
      } else if (phoneType === PhoneTypes.Softphone) {
         if (isHacked) {
            this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsHacked;
         } else if (isFraud) {
            this.fixPhoneInstructionsString = this.fixPhoneIssuesInstructionsFraud;
         }
      }
   }

   private setFixInstructionsForPhone(phone: PhoneRegisterListItem) {
      let isHacked = false;
      let isFraud = false;

      if (this.hackedFraudDetails?.hackedPhones) {
         const hackedPhone = this.hackedFraudDetails.hackedPhones.find((hp) => hp.phoneId === phone.phoneId);
         if (hackedPhone) {
            isHacked = hackedPhone.isHacked ?? false;
            isFraud = hackedPhone.isFraud ?? false;
         }
      }
      this.setInstructionsBasedOnPhoneType(phone.phoneType, isHacked, isFraud, false);
   }

   private setFixInstructions(isHacked: boolean, isFraud: boolean, phonesWithIssues?: HackedPhonesDetails[]) {
      if (phonesWithIssues) {
         phonesWithIssues.forEach((phone) => {
            if (this.onlyDisplayInstructions) {
               this.setInstructionsBasedOnPhoneType(phone.phoneType, isHacked, isFraud, false);
            } else {
               this.setInstructionsBasedOnPhoneType(phone.phoneType, isHacked, isFraud, true);
            }
         });
      }
   }
}
